import { DRAW_DIST } from "./constants";

/** A constant-curvature arc, expressed as fractions of the lap (0..1). dir: +1 right, -1 left. */
export interface CurveSeg {
  s0: number;
  s1: number;
  dir: 1 | -1;
}

/**
 * A hill (or dip, with a negative height) over a stretch of the lap: a smooth bump of `h` screen pixels (measured at the
 * player's row). It changes how the road is drawn; the physics only reacts to it on circuits with `hillPhysics`.
 */
export interface HillSeg {
  s0: number;
  s1: number;
  h: number;
}
export const HILL_MAX = 32; // pixels

/** Road surfaces (prototype, test link only): a stretch of the lap where grip, traction and top speed change. */
export type SurfaceKind = "snow" | "ice" | "water" | "gravel";
export const SURFACE_KINDS: readonly SurfaceKind[] = ["snow", "ice", "water", "gravel"];
export interface SurfaceSeg {
  s0: number;
  s1: number;
  kind: SurfaceKind;
}
/** Multipliers of each surface: engine traction, top speed, braking, steering, centrifugal push, and how long a drifting car keeps sliding (1 = normal). */
export const SURFACE_FX: Record<SurfaceKind, { accel: number; vmax: number; brake: number; steer: number; push: number; slide: number }> = {
  snow: { accel: 0.8, vmax: 0.97, brake: 0.75, steer: 0.9, push: 1.2, slide: 1.4 },
  ice: { accel: 0.6, vmax: 0.98, brake: 0.5, steer: 0.75, push: 1.45, slide: 2 },
  water: { accel: 0.88, vmax: 0.98, brake: 0.8, steer: 0.88, push: 1.25, slide: 1.5 },
  gravel: { accel: 0.9, vmax: 0.92, brake: 0.9, steer: 0.95, push: 1.12, slide: 1.3 },
};

export type Scenery = NonNullable<TrackDef["scenery"]>;
export interface SceneZone {
  s0: number;
  s1: number;
  scenery: Scenery;
}
export const ZONE_MAX = 6;
export const ZONE_MIN_LEN = 0.05;

export interface TrackDef {
  id: number;
  name: string;
  country: string;
  lapUnits: number; // internal distance units for one lap
  laps: number;
  curves: CurveSeg[];
  /** Hills (optional, see HillSeg). */
  hills?: HillSeg[];
  /** When true the hills also act on the driving: climbing slows the car, descending speeds it up (see HILL_GRAVITY). */
  hillPhysics?: boolean;
  /** Road surfaces (optional prototype, see SurfaceSeg). */
  surfaces?: SurfaceSeg[];
  /** Background style drawn behind the horizon. */
  scenery?: "trees" | "harbour" | "city" | "tuscany" | "mountains" | "jungle" | "sunrise" | "forest" | "hills" | "desert" | "seaside" | "airport" | "savanna" | "quebec" | "montreal" | "paris" | "rome" | "toronto" | "manx" | "karting" | "rio" | "paques" | "hawaii" | "tokyo";
  /** Stages only: the backdrop and roadside change along the stage (from s0 to s1 the zone's scenery replaces `scenery`). */
  zones?: SceneZone[];
  /** A point-to-point stage (rally style): one run from A to B, 1 "lap" that does not come back to the start. The minimap is an open path. */
  stage?: boolean;
  /** Hand-tuned scale for the minimap turn rate (rad per lap fraction). Computed if absent. */
  startFraction?: number;
}

/**
 * Course 1 (Australia). Curve intervals were measured by driving the original with its lateral
 * position pinned and recording the road curvature table along one full lap.
 */
export const AUSTRALIA: TrackDef = {
  id: 1,
  name: "AUSTRALIA",
  scenery: "harbour",
  country: "AUS",
  lapUnits: 655360,
  laps: 2,
  curves: [
    { s0: 0.102, s1: 0.2015, dir: 1 },
    { s0: 0.2511, s1: 0.4, dir: 1 },
    { s0: 0.4026, s1: 0.4527, dir: -1 },
    { s0: 0.5024, s1: 0.55, dir: -1 },
    { s0: 0.5522, s1: 0.7017, dir: 1 },
    { s0: 0.7514, s1: 0.8531, dir: 1 },
  ],
};


const C = (s0: number, s1: number, dir: 1 | -1): CurveSeg => ({ s0, s1, dir });
/**
 * Circuits 2..8 are original layouts designed for this remake (the corresponding ROM tables were not
 * recoverable). Lap lengths are multiples of the original 32768-unit half-segment.
 */
export const TRACKS: TrackDef[] = [
  AUSTRALIA,
  { id: 2, name: "BRAZIL", scenery: "jungle", country: "BRA", lapUnits: 524288, laps: 3,
    curves: [C(0.08, 0.16, 1), C(0.2, 0.3, -1), C(0.34, 0.46, 1), C(0.5, 0.58, 1), C(0.62, 0.7, -1), C(0.74, 0.9, 1)] },
  { id: 3, name: "ITALY", scenery: "tuscany", country: "ITA", lapUnits: 786432, laps: 2,
    curves: [C(0.12, 0.18, 1), C(0.3, 0.34, -1), C(0.36, 0.4, 1), C(0.55, 0.75, 1), C(0.85, 0.9, 1)] },
  { id: 4, name: "MONACO", scenery: "city", country: "MON", lapUnits: 458752, laps: 3,
    curves: [C(0.05, 0.1, 1), C(0.13, 0.18, -1), C(0.21, 0.27, 1), C(0.3, 0.34, 1), C(0.38, 0.44, -1), C(0.47, 0.52, -1), C(0.56, 0.66, 1), C(0.7, 0.75, -1), C(0.78, 0.92, 1)] },
  { id: 5, name: "JAPAN", scenery: "sunrise", country: "JPN", lapUnits: 720896, laps: 2,
    curves: [C(0.06, 0.14, 1), C(0.18, 0.26, -1), C(0.28, 0.36, 1), C(0.4, 0.46, -1), C(0.52, 0.58, 1), C(0.62, 0.82, 1), C(0.88, 0.93, -1)] },
  { id: 6, name: "BELGIUM", scenery: "forest", country: "BEL", lapUnits: 589824, laps: 2,
    curves: [C(0.1, 0.22, 1), C(0.3, 0.36, -1), C(0.44, 0.6, 1), C(0.68, 0.74, -1), C(0.78, 0.9, 1)] },
  { id: 7, name: "GERMANY", scenery: "hills", country: "GER", lapUnits: 655360, laps: 2,
    curves: [C(0.07, 0.15, 1), C(0.22, 0.4, 1), C(0.45, 0.5, -1), C(0.55, 0.62, -1), C(0.68, 0.8, 1), C(0.86, 0.92, 1)] },
  { id: 8, name: "SPAIN", scenery: "desert", country: "ESP", lapUnits: 622592, laps: 2,
    curves: [C(0.05, 0.12, -1), C(0.16, 0.28, 1), C(0.34, 0.4, 1), C(0.46, 0.56, -1), C(0.6, 0.8, 1), C(0.86, 0.94, 1)] },
];

/** Number of circuits shipped with the game; indices >= BUILTIN_COUNT are custom circuits made in the admin editor. */
export const BUILTIN_COUNT = TRACKS.length;
/** Indices (into TRACKS) of the circuits currently in the season rotation, in order. Mutated in place by setSeason(). */
export const SEASON: number[] = TRACKS.map((_, i) => i);
export const SCENERIES = ["trees", "harbour", "city", "tuscany", "mountains", "jungle", "sunrise", "forest", "hills", "desert", "seaside", "airport", "savanna", "quebec", "montreal", "paris", "rome", "toronto", "manx", "karting", "rio", "paques", "hawaii", "tokyo"] as const;

/** Sets the season rotation (unknown or duplicate indices are dropped; never empty). */
export function setSeason(ids: number[]): void {
  const seen = new Set<number>();
  const ok = ids.filter((i) => Number.isInteger(i) && i >= 0 && i < TRACKS.length && TRACKS[i] && !seen.has(i) && seen.add(i));
  SEASON.length = 0;
  SEASON.push(...(ok.length ? ok : [0]));
}
/** Registers a (custom) circuit at index idx; idx must not leave holes. Built-in circuits cannot be replaced. */
export function registerTrack(idx: number, def: TrackDef): void {
  if (!Number.isInteger(idx) || idx < BUILTIN_COUNT || idx > TRACKS.length) return;
  TRACKS[idx] = { ...def, id: idx + 1 };
}

export const NAME_MAX = 10;
export const STAGE_MAX_UNITS = 3145728; // a stage may be three times as long as a circuit lap
export const cleanTrackName = (v: unknown) =>
  String(v ?? "").toUpperCase().replace(/[^A-Z0-9 \-]/g, "").replace(/\s+/g, " ").trim().slice(0, NAME_MAX);

/** Returns an error message, or null when the definition is playable. Used by the server (authoritative) and the editor. */
export function validateTrack(d: any): string | null {
  if (!d || typeof d !== "object") return "bad definition";
  if (!cleanTrackName(d.name)) return "name required";
  if (d.scenery !== undefined && !(SCENERIES as readonly string[]).includes(d.scenery)) return "bad scenery";
  if (d.stage !== undefined && typeof d.stage !== "boolean") return "bad stage";
  const maxLen = d.stage ? STAGE_MAX_UNITS : 1048576;
  if (!Number.isInteger(d.lapUnits) || d.lapUnits < 262144 || d.lapUnits > maxLen || d.lapUnits % 32768 !== 0) return `${d.stage ? "stage" : "lap"} length must be a multiple of 32768 between 262144 and ${maxLen}`;
  if (d.stage ? d.laps !== 1 : !Number.isInteger(d.laps) || d.laps < 1 || d.laps > 5) return d.stage ? "a stage is a single run (1 lap)" : "laps must be 1-5";
  if (!Array.isArray(d.curves) || d.curves.length < 1 || d.curves.length > 14) return "1 to 14 curves";
  let prevEnd = -1, net = 0;
  const cs = [...d.curves].sort((a: CurveSeg, b: CurveSeg) => a.s0 - b.s0);
  for (const c of cs) {
    if (!c || typeof c.s0 !== "number" || typeof c.s1 !== "number" || (c.dir !== 1 && c.dir !== -1)) return "bad curve";
    if (!(c.s0 >= 0.04 && c.s1 <= 0.97 && c.s1 > c.s0)) return "curves must lie between 4% and 97% of the lap";
    if (c.s1 - c.s0 < 0.03) return "a curve is too short (min 3% of the lap)";
    if (prevEnd >= 0 && c.s0 - prevEnd < 0.002) return "curves overlap";
    prevEnd = c.s1;
    net += c.dir * (c.s1 - c.s0);
  }
  if (!d.stage && Math.abs(net) < 0.15) return "curves must not cancel out: add more turns in one direction";
  if (d.hillPhysics !== undefined && typeof d.hillPhysics !== "boolean") return "bad hillPhysics";
  if (d.zones !== undefined) {
    if (!d.stage) return "scenery zones are for stages";
    if (!Array.isArray(d.zones) || d.zones.length > ZONE_MAX) return `0 to ${ZONE_MAX} scenery zones`;
    let end = 0;
    for (const z of [...d.zones].sort((a: SceneZone, b: SceneZone) => a.s0 - b.s0)) {
      if (!z || typeof z.s0 !== "number" || typeof z.s1 !== "number" || !(SCENERIES as readonly string[]).includes(z.scenery)) return "bad scenery zone";
      if (!(z.s0 >= 0 && z.s1 <= 1 && z.s1 - z.s0 >= ZONE_MIN_LEN - 1e-9)) return `a scenery zone must span at least ${ZONE_MIN_LEN * 100}% of the stage`;
      if (z.s0 < end - 1e-9) return "scenery zones overlap";
      end = z.s1;
    }
  }
  if (d.surfaces !== undefined) {
    if (!Array.isArray(d.surfaces) || d.surfaces.length > 8) return "0 to 8 surfaces";
    let end = 0;
    for (const z of [...d.surfaces].sort((a: SurfaceSeg, b: SurfaceSeg) => a.s0 - b.s0)) {
      if (!z || typeof z.s0 !== "number" || typeof z.s1 !== "number" || !(SURFACE_KINDS as readonly string[]).includes(z.kind)) return "bad surface";
      if (!(z.s0 >= 0 && z.s1 <= 1 && z.s1 - z.s0 >= 0.02)) return "a surface must span at least 2% of the lap";
      if (z.s0 < end - 1e-9) return "surfaces overlap";
      end = z.s1;
    }
  }
  if (d.hills !== undefined) {
    if (!Array.isArray(d.hills) || d.hills.length > 8) return "0 to 8 hills";
    for (const h of d.hills) {
      if (!h || typeof h.s0 !== "number" || typeof h.s1 !== "number" || typeof h.h !== "number") return "bad hill";
      if (!(h.s0 >= 0.02 && h.s1 <= 0.98 && h.s1 - h.s0 >= 0.03)) return "a hill must span at least 3% of the lap, between 2% and 98%";
      if (!(Math.abs(h.h) >= 1 && Math.abs(h.h) <= HILL_MAX)) return `hill height must be 1 to ${HILL_MAX}`;
    }
  }
  return null;
}
/** Clean copy of a validated definition (numbers rounded, curves sorted). */
export function normalizeTrack(d: TrackDef, keepSurfaces = false): Omit<TrackDef, "id"> {
  return {
    name: cleanTrackName(d.name),
    country: String(d.country ?? "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3) || "CUS",
    scenery: d.scenery ?? "trees",
    lapUnits: d.lapUnits,
    laps: d.laps,
    ...(d.stage ? { stage: true } : {}),
    curves: [...d.curves].sort((a, b) => a.s0 - b.s0).map((c) => ({ s0: Math.round(c.s0 * 1000) / 1000, s1: Math.round(c.s1 * 1000) / 1000, dir: c.dir })),
    ...(d.zones && d.zones.length ? { zones: [...d.zones].sort((a, b) => a.s0 - b.s0).map((z) => ({ s0: Math.round(z.s0 * 1000) / 1000, s1: Math.round(z.s1 * 1000) / 1000, scenery: z.scenery })) } : {}),
    ...(keepSurfaces && d.surfaces && d.surfaces.length ? { surfaces: [...d.surfaces].sort((a, b) => a.s0 - b.s0).map((z) => ({ s0: Math.round(z.s0 * 1000) / 1000, s1: Math.round(z.s1 * 1000) / 1000, kind: z.kind })) } : {}),
    ...(d.hills && d.hills.length && d.hillPhysics ? { hillPhysics: true } : {}),
    ...(d.hills && d.hills.length ? { hills: [...d.hills].sort((a, b) => a.s0 - b.s0).map((h) => ({ s0: Math.round(h.s0 * 1000) / 1000, s1: Math.round(h.s1 * 1000) / 1000, h: Math.round(h.h * 10) / 10 })) } : {}),
  } as Omit<TrackDef, "id">;
}
/** Variants: "mirror" flips left/right; "reverse" drives the same road the other way round (order reversed, turns flipped); "both" does both. */
export function variantOf(t: TrackDef, kind: "mirror" | "reverse" | "both"): Omit<TrackDef, "id"> {
  const flipSide = kind === "mirror" || kind === "both";
  const rev = kind === "reverse" || kind === "both";
  const curves = t.curves
    .map((c) => (rev ? { s0: 1 - c.s1, s1: 1 - c.s0, dir: (-c.dir) as 1 | -1 } : { ...c }))
    .map((c) => (flipSide ? { ...c, dir: (-c.dir) as 1 | -1 } : c))
    .sort((a, b) => a.s0 - b.s0)
    .map((c) => ({ s0: Math.round(c.s0 * 1000) / 1000, s1: Math.round(c.s1 * 1000) / 1000, dir: c.dir }));
  const suf = kind === "mirror" ? "-M" : kind === "reverse" ? "-R" : "-MR";
  const hills = (t.hills ?? []).map((h) => (rev ? { s0: Math.round((1 - h.s1) * 1000) / 1000, s1: Math.round((1 - h.s0) * 1000) / 1000, h: h.h } : { ...h })).sort((a, b) => a.s0 - b.s0);
  return { name: (t.name.slice(0, NAME_MAX - suf.length) + suf).replace(/ -/g, "-"), country: t.country, scenery: t.scenery, lapUnits: t.lapUnits, laps: t.laps, ...(t.stage ? { stage: true } : {}), ...(t.zones && t.zones.length ? { zones: t.zones.map((z) => (rev ? { s0: Math.round((1 - z.s1) * 1000) / 1000, s1: Math.round((1 - z.s0) * 1000) / 1000, scenery: z.scenery } : { ...z })).sort((a, b) => a.s0 - b.s0) } : {}), curves, ...(hills.length ? { hills } : {}), ...(hills.length && t.hillPhysics ? { hillPhysics: true } : {}) };
}

/** Season points for finishing positions 1..10. */
export const POINTS = [10, 8, 6, 5, 4, 3, 2, 1, 0, 0];

/** Curvature (-1,0,+1) at lap fraction f in [0,1). */
export function curvatureAt(track: TrackDef, f: number): number {
  f = f - Math.floor(f);
  for (const c of track.curves) if (f >= c.s0 && f < c.s1) return c.dir;
  return 0;
}

/** Road elevation (screen pixels at the player's row) at a position in units; 0 on a track without hills. */
export function elevationAtPos(track: TrackDef, pos: number): number {
  const hills = track.hills;
  if (!hills || !hills.length) return 0;
  let f = pos / track.lapUnits;
  f -= Math.floor(f);
  let e = 0;
  for (const h of hills) {
    if (f > h.s0 && f < h.s1) {
      // smooth bump 16 t²(1-t)², zero slope at both ends; plain arithmetic only (no sin/cos: they differ between browsers, and the physics must be exact everywhere)
      const t = (f - h.s0) / (h.s1 - h.s0);
      const u = t * (1 - t);
      e += h.h * 16 * u * u;
    }
  }
  return e;
}

/** The scenery shown at a position (stages: the zone it falls in; otherwise the track's own). */
export function sceneryAt(track: TrackDef, pos: number): Scenery {
  const base: Scenery = track.scenery ?? "trees";
  const z = track.zones;
  if (!z || !z.length) return base;
  const f = pos / track.lapUnits;
  for (const q of z) if (f >= q.s0 && f < q.s1) return q.scenery;
  return base;
}
/** The backdrop at a position: the scenery, and while the horizon crosses a zone edge a blend towards the next one (t from 0 to 1). */
export function backdropAt(track: TrackDef, pos: number, half = 2400): { a: Scenery; b: Scenery; t: number } {
  const here = sceneryAt(track, pos);
  if (!track.zones || !track.zones.length) return { a: here, b: here, t: 0 };
  for (const q of track.zones) {
    for (const e of [q.s0, q.s1]) {
      const at = e * track.lapUnits;
      if (pos > at - half && pos < at + half) {
        const a = sceneryAt(track, at - 1), b = sceneryAt(track, at + 1);
        if (a !== b) return { a, b, t: (pos - (at - half)) / (2 * half) };
      }
    }
  }
  return { a: here, b: here, t: 0 };
}

/** Curvature at an absolute position in units (wraps around the lap). */
export function curvatureAtPos(track: TrackDef, pos: number): number {
  return curvatureAt(track, pos / track.lapUnits);
}

/**
 * The curve a pilot is at: its number in the lap (1 = the first curve after the start line), and whether he is inside it right now.
 * Between two curves it is the last one he passed (before the first curve of a lap: the last curve of the lap before). null when the circuit has no curve.
 */
export function curveNumberAt(track: TrackDef, pos: number): { n: number; total: number; inside: boolean } | null {
  const cs = [...track.curves].sort((a, b) => a.s0 - b.s0);
  if (!cs.length) return null;
  const f = (((pos / track.lapUnits) % 1) + 1) % 1;
  let n = cs.length; // before the first curve: the last of the previous lap
  let inside = false;
  for (let i = 0; i < cs.length; i++) {
    if (f >= cs[i].s0) {
      n = i + 1;
      inside = f <= cs[i].s1;
    }
  }
  return { n, total: cs.length, inside };
}

/** Surface under a position (absolute units; wraps around the lap), or null on plain tarmac. */
export function surfaceAtPos(track: TrackDef, pos: number): SurfaceKind | null {
  if (!track.surfaces) return null;
  const f = (((pos / track.lapUnits) % 1) + 1) % 1;
  for (const z of track.surfaces) if (f >= z.s0 && f < z.s1) return z.kind;
  return null;
}

/** Next curve ahead of f: returns {dir, distanceUnits} or null. Used for the warning sign. */
export function nextCurve(track: TrackDef, pos: number): { dir: 1 | -1; dist: number } | null {
  const f = (((pos / track.lapUnits) % 1) + 1) % 1;
  let best: { dir: 1 | -1; dist: number } | null = null;
  for (const c of track.curves) {
    let d = c.s0 - f;
    if (d < 0) d += 1;
    if (f >= c.s0 && f < c.s1) continue; // already inside
    const dist = d * track.lapUnits;
    if (!best || dist < best.dist) best = { dir: c.dir, dist };
  }
  return best;
}

/**
 * Rough minimap polyline: integrates heading with turn rate K so the net heading change is one full
 * clockwise revolution, then removes the position residual so the loop closes.
 */
export function minimapPoints(track: TrackDef, n = 240): { x: number; y: number }[] {
  let net = 0, sum = 0;
  for (const c of track.curves) (net += c.dir * (c.s1 - c.s0)), (sum += c.s1 - c.s0);
  // a stage is an open path: its turns keep their own rate (a half turn when it bends all the way) and nothing is closed
  const K = track.stage ? (Math.PI * 0.9) / Math.max(sum, 0.2) : (2 * Math.PI) / net;
  const pts: { x: number; y: number }[] = [];
  let x = 0,
    y = 0,
    th = 0;
  const ds = 1 / n;
  for (let i = 0; i < n; i++) {
    pts.push({ x, y });
    const k = curvatureAt(track, (i + 0.5) * ds);
    th += k * K * ds;
    x += Math.cos(th) * ds;
    y += Math.sin(th) * ds;
  }
  if (track.stage) return pts;
  // close the loop: distribute the residual linearly
  const rx = x,
    ry = y;
  for (let i = 0; i < n; i++) {
    pts[i].x -= (rx * i) / n;
    pts[i].y -= (ry * i) / n;
  }
  return pts;
}

export { DRAW_DIST };
